<div class="entity-compliance-answers-container">
  <!-- Loading State -->
  <div v-if="is_loading_overview" class="text-center py-4">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="mt-2">Loading compliance data...</p>
  </div>

  <!-- No Data State -->
  <div v-else-if="!has_data" class="text-center py-4">
    <p>No compliance rules assigned to this entity.</p>
  </div>

  <!-- Main Content -->
  <div v-else>
    <!-- Header Summary -->
    <div class="glass-card border border-light border-1 rounded-lg mb-3 p-3 shadow">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <p class="text-x fw-bold mb-0">Compliance Progress</p>
        </div>
        <div class="d-flex align-items-center gap-2">
          <span class="status-badge text-xs manually-verified">{{ total_rules }} Rules</span>
          <button
            class="btn btn-primary-dark btn-sm rounded text-xxs shadow"
            @click="sync_answers"
            :disabled="is_syncing"
          >
            <span v-if="is_syncing" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="fa-solid fa-sync-alt me-1"></i>
            Sync Answers
          </button>
        </div>
      </div>

      <!-- Progress Bar -->
      <div class="progress-bar-container">
        <div class="progress-bar-fill" :style="progress_bar_style"></div>
      </div>

      <div class="d-flex justify-content-between align-items-center mt-2">
        <div class="d-flex gap-3">
          <span class="text-xxs fw-bold">
            <i class="bi bi-check-circle-fill text-success me-1"></i>
            {{ total_answered }} Answered
          </span>
          <span class="text-xxs fw-bold">
            {{ total_unanswered }} Pending
          </span>
        </div>
        <span class="text-xxs fw-bold text-end">{{ completion_percentage }}% Complete</span>
      </div>
    </div>

    <!-- Search -->
    <div class="mb-3 d-none">
      <input
        type="text"
        class="form-control form-control-sm text-xxs rounded"
        placeholder="Search rules..."
        v-model="search_query"
      />
    </div>

    <!-- Rules List -->
    <div v-if="!filtered_rules.length" class="text-center py-4">
      <p>No rules match your search.</p>
    </div>

    <div v-else v-for="rule_item in filtered_rules" :key="rule_item.id" class="mb-2">
      <div class="card shadow border-0 rounded-lg p-3">
        <!-- Rule Header -->
        <div class="section-header text-xs fw-bold border-bottom border-2 pb-2 mb-3">
          <div
            class="d-flex justify-content-between align-items-center mb-3 clickable-header"
            @click="toggle_rule_expand(rule_item.id)"
          >
            <!-- Left: Icon + Title -->
            <div class="d-flex align-items-center gap-2">
              <span :class="rule_item.answered_count === rule_item.total_questions ? 'text-success' : 'text-danger'" class="fs-4">
                <i :class="rule_item.answered_count === rule_item.total_questions ? 'bi bi-check-circle' : 'bi bi-exclamation-circle'"></i>
              </span>
              <div class="d-flex flex-column">
                <span class="text-capitalize text-x fw-bold">{{ rule_item.name }}</span>
                <p class="text-xxs text-muted mb-0" v-if="rule_item.description">
                  {{ truncate_text(rule_item.description, 80) }}
                </p>
              </div>
            </div>

           <!-- Right: Badge + Chevron -->
            <div class="d-flex align-items-center gap-3">
              <!-- Evaluation Dates -->
              <div class="evaluation-dates d-flex gap-3 me-3" v-if="rule_evaluation_dates[rule_item.id]">
                <div class="last-evaluation text-end">
                  <span class="text-xxs text-muted d-block">Last</span>
                  <span class="text-xxs fw-bold">
                    <i class="bi bi-calendar-check me-1"></i>
                    {{ rule_evaluation_dates[rule_item.id].last_evaluation ? format_date(rule_evaluation_dates[rule_item.id].last_evaluation) : 'Not set' }}
                  </span>
                </div>
                <div class="next-evaluation text-end">
                  <span class="text-xxs text-muted d-block">Next</span>
                  <span class="text-xxs fw-bold" :class="get_next_evaluation_class(rule_evaluation_dates[rule_item.id].next_evaluation)">
                    <i class="bi bi-calendar-week me-1"></i>
                    {{ rule_evaluation_dates[rule_item.id].next_evaluation ? format_date(rule_evaluation_dates[rule_item.id].next_evaluation) : 'Not set' }}
                  </span>
                </div>
              </div>

              <span
                class="status-badge text-xs"
                :class="rule_item.answered_count === rule_item.total_questions ? 'manually-verified' : 'pending'"
              >
                {{ rule_item.answered_count }}/{{ rule_item.total_questions }}
              </span>
              <i :class="is_rule_expanded(rule_item.id) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </div>
          </div>

          <!-- Progress Preview Section (always visible) -->
          <div class="mt-3 pt-2 border-top">
            <div class="p-2 rounded bg-light">
              <div class="d-flex align-items-center gap-2 mb-2">
                <span class="text-xs text-dark">
                  {{ rule_item.answered_count }} of {{ rule_item.total_questions }} questions answered
                </span>
              </div>

              <!-- Mini progress bar -->
              <div class="progress-bar-container">
                <div
                  class="progress-bar-fill"
                  :class="get_progress_color_class(rule_item.total_questions > 0 ? (rule_item.answered_count / rule_item.total_questions * 100) : 0)"
                  :style="get_progress_bar_width_style(rule_item.answered_count, rule_item.total_questions)"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Rule Body (Expanded) -->
        <div v-if="is_rule_expanded(rule_item.id)">
          <!-- Loading Details -->
          <div v-if="is_rule_loading(rule_item.id)" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
            <p class="text-xxs mt-2">Loading questions...</p>
          </div>

          <!-- Rule Details -->
          <div v-else-if="get_rule_details(rule_item.id)">
            <!-- Sub-Sections -->
            <div class="sub-sections-container mt-3">
              <div
                v-for="sub_section in get_all_sub_sections_for_rule(rule_item.id)"
                :key="`${rule_item.id}-${sub_section.section_id}`"
                class="mb-3"
              >
                <!-- Sub-Section Collapsible Header -->
                <div class="sub-section-card border border-1 rounded-lg shadow-sm">
                  <div
                    class="sub-section-header d-flex justify-content-between align-items-center p-2 border-bottom"
                    @click="toggle_sub_section_collapse(`${rule_item.id}-${sub_section.section_id}`)"
                  >
                    <div class="d-flex align-items-center gap-2">
                      <span class="text-primary">
                        <i :class="is_sub_section_collapsed(`${rule_item.id}-${sub_section.section_id}`) ? 'bi bi-chevron-right' : 'bi bi-chevron-down'"></i>
                      </span>
                      <span class="text-xs fw-bold">{{ sub_section.section_name }}</span>
                    </div>
                    <span class="badge bg-light text-dark border text-xxs">{{ sub_section.questions.length }} questions</span>
                  </div>

                  <!-- Questions -->
                  <div
                    v-show="!is_sub_section_collapsed(`${rule_item.id}-${sub_section.section_id}`)"
                    class="p-3"
                  >
                    <div
                      v-for="(question, question_index) in sub_section.questions"
                      :key="question.section_question_id"
                      class="mb-3 ms-2"
                    >
                      <div class="d-flex justify-content-between align-items-start">
                        <div class="d-flex flex-grow-1 align-items-start">
                        <span
                          :class="question.status === 'Verified' ? 'text-success' : (question.status === 'Changes Requested' ? 'text-danger' : 'text-muted')"
                          class="me-2 fs-6"
                        >
                          <i
                            :class="question.status === 'Verified' ? 'bi bi-check-circle-fill' : (question.status === 'Changes Requested' ? 'bi bi-x-circle-fill' : (question.answer ? 'bi bi-check-circle' : 'bi bi-circle'))"
                          ></i>
                        </span>

                        <div class="flex-grow-1">
                          <div class="d-flex align-items-start justify-content-between gap-2">
                            <label class="text-dark text-xs text-capitalize fw-7 mb-1">
                              {{ question_index + 1 }}. {{ question.question_text }}
                            </label>
                            <div v-if="question.date_answered" class="text-xxs text-muted">
                              <i class="bi bi-calendar-check me-1"></i>
                              {{ format_datetime(question.date_answered) }}
                            </div>
                          </div>

                          <div class="d-flex align-items-center gap-2 mt-1">
                            <span
                               class="status-pill text-xxs"
                               :class="get_answer_source_badge_class(question.status || question.answer_source)"
                             >
                               <i :class="get_answer_source_icon(question.status || question.answer_source)"></i>
                               <span>{{ get_answer_source_label(question.status || question.answer_source) }}</span>
                             </span>
                             <span v-if="question.latest_verification_note" class="text-xxs text-info fst-italic">
                               <i class="bi bi-info-circle me-1"></i>Note: {{ question.latest_verification_note }}
                             </span>
                          </div>

                          <div class="mt-2">
                            <div v-if="!should_show_inline_input(question)">
                               <div v-if="question.answer || ['single_select', 'multi_select', 'checkboxes'].includes(question.question_type_raw)" class="mt-1">
                                  <div v-if="is_file_type(question.question_type_raw)" class="d-flex align-items-center gap-2 mt-2">
                                    <span class="text-xxs text-muted fw-6">{{ get_file_name_from_url(question.answer) }}</span>
                                    <button class="btn btn-primary-dark btn-sm rounded text-xxs shadow" @click.stop="open_file_preview(question.answer, get_file_name_from_url(question.answer))">Preview file</button>
                                  </div>
                                  <div v-else-if="['single_select'].includes(question.question_type_raw)" class="d-flex flex-column gap-1 mt-1">
                                    <div v-for="choice in question.multiselect_choices" :key="choice" class="form-check">
                                      <input class="form-check-input" type="radio" :checked="question.answer === choice" disabled>
                                      <label class="form-check-label text-xxs opacity-75 text-dark">{{ choice }}</label>
                                    </div>
                                  </div>
                                  <div v-else-if="['multi_select', 'checkboxes'].includes(question.question_type_raw)" class="d-flex flex-column gap-1 mt-1">
                                    <div v-for="choice in question.multiselect_choices" :key="choice" class="form-check">
                                      <input class="form-check-input" type="checkbox" :checked="(question.answer || '').split(',').map(s => s.trim()).includes(choice)" disabled>
                                      <label class="form-check-label text-xxs opacity-75 text-dark">{{ choice }}</label>
                                    </div>
                                  </div>
                                  <div v-else class="text-xxs text-muted fst-italic answer-text">Answer: {{ question.answer }}</div>
                               </div>

                               <div class="mt-2 d-flex flex-wrap gap-2">
                                  <button class="btn btn-primary-dark btn-sm rounded text-xxs shadow btn-outline-primary" @click="start_editing(question)">
                                    <i class="bi bi-pencil"></i> {{ question.answer ? 'Change Answer' : 'Submit Answer' }}
                                  </button>
                                  <template v-if="question.answer && question.status === 'Submitted'">
                                    <button class="btn btn-primary-dark btn-sm rounded text-xxs shadow btn-outline-success" @click="open_verification_modal(question.answer_id, question.status_id || 'Verified')">Approve</button>
                                    <button class="btn btn-primary-dark btn-sm rounded text-xxxs shadow btn-outline-danger" @click="open_verification_modal(question.answer_id, question.status_id || 'Changes Requested')">Request Changes</button>
                                  </template>
                                  <button v-if="question.answer" class="btn btn-primary-dark btn-sm rounded text-xxs shadow btn-outline-info" @click="open_history_modal(question)">
                                    <i class="bi bi-clock-history"></i> History
                                  </button>
                               </div>
                            </div>

                            <div v-else class="bg-light p-3 rounded border mt-2">
                               <div v-if="['text', 'short_text', 'textarea', 'long_text', 'text_area', 'email', 'url', 'number', 'date', 'short text', 'long text'].includes(question.question_type_raw)">
                                 <textarea v-if="['textarea', 'long_text', 'text_area', 'long text'].includes(question.question_type_raw)" class="form-control form-control-sm text-xxs" rows="3" v-model="edit_answer_value" placeholder="Type answer here..."></textarea>
                                 <input v-else :type="['date'].includes(question.question_type_raw) ? 'date' : (['number'].includes(question.question_type_raw) ? 'number' : 'text')" class="form-control form-control-sm text-xxs" v-model="edit_answer_value" :placeholder="question.question_type_raw === 'date' ? '' : 'Type answer here...'">
                               </div>
                               <div v-else-if="['multi_select', 'checkboxes', 'single_select'].includes(question.question_type_raw)">
                                 <div v-if="question.question_type_raw === 'single_select'" class="d-flex flex-column gap-1">
                                   <div v-for="choice in question.multiselect_choices" :key="choice" class="form-check">
                                     <input class="form-check-input" type="radio" :name="'edit-choice-' + question.id" :value="choice" v-model="edit_answer_value">
                                     <label class="form-check-label text-xxs">{{ choice }}</label>
                                   </div>
                                 </div>
                                 <div v-else class="d-flex flex-column gap-1">
                                   <div v-for="choice in question.multiselect_choices" :key="choice" class="form-check">
                                     <input class="form-check-input" type="checkbox" :checked="edit_selected_choices.includes(choice)" @change="toggle_edit_choice(choice)">
                                     <label class="form-check-label text-xxs">{{ choice }}</label>
                                   </div>
                                 </div>
                                </div>
                               <div v-else-if="is_file_type(question.question_type_raw)">
                                 <input type="file" class="form-control form-control-sm text-xxs" @change="handle_edit_file_change">
                               </div>

                               <div class="d-flex gap-2 mt-3">
                                 <button class="btn btn-primary-dark btn-sm rounded text-xxs shadow" @click="submit_answer(question)" :disabled="is_submitting_answer">
                                   <span v-if="is_submitting_answer" class="spinner-border spinner-border-sm me-1"></span>
                                   Submit
                                 </button>
                                 <button class="btn btn-outline-secondary btn-sm text-xxs rounded" @click="cancel_editing">Cancel</button>
                               </div>
                            </div>
                          </div>
                        </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- File Preview Modal -->
  <div v-if="show_file_preview" @click.self="close_file_preview">
    <FilePreviewComponent
      :presigned_url="preview_file_url"
      :file_name="preview_file_name"
      @close="close_file_preview"
      :key="preview_file_url"
    />
  </div>

  <!-- Verification Note Modal -->
  <div v-if="show_verification_modal" class="modal d-block" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow-lg">
        <div class="modal-header bg-dark text-white p-2">
          <h6 class="modal-title text-sm fw-bold">{{ verifying_status === 'verified' ? 'Approve' : 'Reject' }}</h6>
          <button type="button" class="btn-close btn-close-white" @click="close_verification_modal"></button>
        </div>
        <div class="modal-body p-3">
          <textarea class="form-control form-control-sm text-xxs" rows="3" v-model="verification_note" placeholder="Notes..."></textarea>
        </div>
        <div class="modal-footer p-2 border-0">
          <button class="btn btn-outline-secondary btn-sm text-xxs" @click="close_verification_modal">Cancel</button>
          <button class="btn btn-primary-dark btn-sm text-xxs" @click="submit_verification" :disabled="is_verifying">Confirm</button>
        </div>
      </div>
    </div>
  </div>

  <!-- History Modal -->
  <ComplianceAnswerHistory
    v-if="show_history_modal"
    :question="history_question"
    :entity_profile_id="entity_profile_id"
    @close="show_history_modal = false"
  />
</div>